import { useMemo } from 'react'
import { BufferGeometry, Line, Mesh, Vector3 } from 'three'
import { RectangleXYZ } from '@modugen/ui-toolkit'
import { useDisposeThreeObjectsOnUnmount } from '@editorHooks'
import useVisibleModel from 'src/components/pages/Editor/hooks/useVisibleModel'

interface Props {
  xyOnly?: boolean
}

/**
 * Creates snap targets from all wall opening edges and corner points
 */
const useWallOpeningsSnapTargets = ({ xyOnly = false }: Props = {}) => {
  const model = useVisibleModel()

  const snapTargets = useMemo(() => {
    const targets: (Line | Mesh)[] = []

    // Iterate through all walls
    model.walls.forEach(wall => {
      // Iterate through all openings in each wall
      wall.openings?.forEach(opening => {
        const points = opening.shape.points as RectangleXYZ

        // Create corner point snap targets
        points.forEach((point, index) => {
          const p = xyOnly ? new Vector3(point.x, point.y, 0) : point.v

          const geometry = new BufferGeometry().setFromPoints([p])
          const cornerMesh = new Mesh(geometry)
          cornerMesh.userData.wallGuid = wall.guid
          cornerMesh.userData.openingGuid = opening.guid
          cornerMesh.userData.cornerIndex = index
          cornerMesh.userData.type = 'opening-corner'
          targets.push(cornerMesh)
        })

        // Create edge line snap targets
        points.forEach((start, index) => {
          const end = points[(index + 1) % points.length]

          const p1 = xyOnly
            ? new Vector3(start.x, start.y, 0)
            : new Vector3(start.x, start.y, start.z)
          const p2 = xyOnly ? new Vector3(end.x, end.y, 0) : new Vector3(end.x, end.y, end.z)

          const geometry = new BufferGeometry().setFromPoints([p1, p2])
          const line = new Line(geometry)
          line.userData.wallGuid = wall.guid
          line.userData.openingGuid = opening.guid
          line.userData.edgeIndex = index
          line.userData.type = 'opening-edge'

          targets.push(line)
        })
      })
    })

    return targets
  }, [model.walls, xyOnly])

  useDisposeThreeObjectsOnUnmount(snapTargets)

  return snapTargets
}

export default useWallOpeningsSnapTargets
